<template>
    <div class="MatchForm">
        <div class="flex row-left-top mb-2">
            <div class="flex-1">
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('schema.creation.form.type.label') }}
                </WText>
                <WSelect
                    v-model="type"
                    name="matchTo"
                    :placeholder="$t('schema.creation.form.type.placeholder')"
                    :options="normalizeTypesOptions"
                    :disabled="isLoading || !(normalizeTypesOptions || []).length"
                />
            </div>
            <div class="flex-1 ml-1">
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('schema.creation.form.autoMatch.label') }}
                </WText>
                <div>
                    <WSwitch
                        v-model="autoMatch"
                        name="autoMatch"
                        :disabled="isLoading"
                        color="accent-blue"
                    />
                </div>
            </div>
        </div>
        <div class="flex row-left-top mb-2">
            <div class="flex-1">
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('schema.creation.form.matchTo.label') }}
                </WText>
                <WSelect
                    v-model="matchTo"
                    name="matchTo"
                    :placeholder="$t('schema.creation.form.matchTo.placeholder')"
                    :options="normalizeFieldsOptions"
                    :disabled="isLoading || !(normalizeFieldsOptions || []).length"
                />
            </div>
            <div class="flex-1 ml-1">
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('schema.creation.form.fields.label') }}
                </WText>
                <WSelect
                    v-model="fields"
                    name="matchTo"
                    :placeholder="$t('schema.creation.form.fields.placeholder')"
                    :options="normalizeFieldsOptions"
                    :disabled="isLoading || !(normalizeFieldsOptions || []).length"
                    multiple
                />
            </div>
        </div>

        <div class="flex row-left-top mb-2">
            <div class="flex-1">
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('schema.creation.form.against.label') }}
                </WText>
                <WSelect
                    v-model="againstEntity"
                    name="against"
                    :placeholder="$t('schema.creation.form.against.placeholder')"
                    :options="entities"
                    :disabled="isLoading || !(entities || []).length"
                    @input="handleChangeAgainstEntity"
                />
            </div>
            <div class="flex-1 ml-1" />
        </div>

        <FadeHeightTransition mode="out-in">
            <div
                v-if="againstEntity"
                class="flex row-left-top mb-2"
            >
                <div class="flex-1">
                    <WText
                        tag="label"
                        typo="label"
                        :size="14"
                    >
                        {{ $t('schema.creation.form.matchFrom.label') }}
                    </WText>
                    <WSelect
                        v-model="matchFrom"
                        name="matchFrom"
                        :placeholder="$t('schema.creation.form.matchFrom.placeholder')"
                        :options="againstOptions"
                        :disabled="isLoading || !(normalizeFieldsOptions || []).length"
                    />
                </div>
                <div class="flex-1 ml-1">
                    <WText
                        tag="label"
                        typo="label"
                        :size="14"
                    >
                        {{ $t('schema.creation.form.fields.label') }}
                    </WText>
                    <WSelect
                        v-model="againstFields"
                        name="matchTo"
                        :placeholder="$t('schema.creation.form.fields.placeholder')"
                        :options="againstOptions"
                        :disabled="isLoading || !(againstOptions || []).length"
                        multiple
                    />
                </div>
            </div>
        </FadeHeightTransition>

        <div
            v-if="enableLocked"
            class="flex row-left-top mb-2"
        >
            <div
                class="flex-1"
            >
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('schema.creation.form.locked.label') }}
                </WText>
                <div>
                    <WSwitch
                        v-model="locked"
                        name="locked"
                        :disabled="isLoading"
                        color="accent-blue"
                    />
                </div>
            </div>
        </div>

        <div class="flex row-right-center">
            <WButton
                outline
                color="accent-blue"
                :loading="isLoading"
                @click="$emit('onReset')"
            >
                {{ $t('entities.creation.reset') }}
            </WButton>
            <WButton
                color="primary"
                :loading="isLoading"
                class="ml-2"
                @click="handleSubmit"
            >
                {{ $t('entities.creation.submit') }}
            </WButton>
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { Api, apiRequest } from '@/api'

export const ACTIONS = {
    CREATE: 'CREATE',
    UPDATE: 'CREATE',
}

const ENABLE_LOCKED = false

export default {
    name: 'MatchForm',
    mixins: [validationMixin],
    props: {
        entityKey: VueTypes.string.required,
        node: VueTypes.object.required,
        selectGroups: VueTypes.any.required,
        action: VueTypes.string.def(ACTIONS.CREATE),
    },
    validations () {
        return {
            type: { required },
        }
    },
    data () {
        const { against, ...form } = this.node || {}

        return {
            key: undefined,
            type: '',
            matchFrom: '',
            matchTo: '',
            fields: '',
            autoMatch: undefined,
            locked: undefined,
            ...form,

            entities: [],
            againstEntity: (against || {}).entity || '',
            againstFields: (against || {}).fields || '',
            againstOptions: [],

            enableLocked: ENABLE_LOCKED,
            isFetching: false,
            isLoading: false,
        }
    },
    computed: {
        normalizeFieldsOptions () {
            return this.normalizeSelectGroup((this.selectGroups || {}).fields)
        },
        normalizeTypesOptions () {
            return this.normalizeSelectGroup((this.selectGroups || {}).types)
        },
    },
    mounted () {
        this.getEntities()

        if (((this.node || {}).against || {}).entity) {
            this.getSchemaEntity(this.node.against.entity)
        }
    },
    methods: {
        handleChangeAgainstEntity (entityKey) {
            this.getSchemaEntity(entityKey)
        },
        normalizeSelectGroup (selectGroup = {}) {
            return Object.keys(selectGroup).map(key => ({ key, label: selectGroup[key] }))
        },
        normalizeAgainsFieldsOptions ({ fields }) {
            return (fields || []).map(({ key, name }) => ({ key, label: name }))
        },
        async getEntities () {
            this.isFetching = true
            apiRequest(Api().entities.list())
                .then(({ data }) => (this.entities = data))
                .finally(() => (this.isFetching = false))
        },
        async getSchemaEntity (entityKey) {
            this.isFetching = true
            apiRequest(Api().entities.schema({ entityKey }))
                .then(({ data }) => (this.againstOptions = this.normalizeAgainsFieldsOptions(data)))
                .finally(() => (this.isFetching = false))
        },
        async handleSubmit () {
            this.$v.$touch()

            if (!this.$v.$invalid) {
                this.isLoading = true
                const params = {
                    key: this.key,
                    type: this.type,
                    matchFrom: this.matchFrom,
                    matchTo: this.matchTo,
                    fields: this.fields,
                    against: {
                        entity: this.againstEntity,
                        fields: this.againstFields,
                    },
                    autoMatch: this.autoMatch,
                    ...(ENABLE_LOCKED ? { locked: this.locked } : {}),
                }

                const method = ACTIONS.CREATE === this.action ? 'matchRuleCreate' : 'matchRuleUpdate'
                apiRequest(Api().entities[method]({ entityKey: this.entityKey, ruleKey: this.key, params }))
                    .then(data => this.$emit('onSubmit', data))
                    .catch(() => this.$emit('onError'))
                    .finally(() => (this.isLoading = false))
            }
        },
    },
}
</script>
